import type { DragEndEvent } from '@dnd-kit/core'
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Globe, Loader2, Minus, Plus, Upload } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { addPlaceImageFile, addPlaceImageUrl, deletePlaceImage, reorderPlaceImages } from '@/api/services/overland'
import type { PlaceImage } from '@/api/types/overland'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { AuthImage } from '@/components/ui/auth-image'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useSubmitLock } from '@/hooks/useSubmitLock'

type Props = {
  placeId: number
  images: PlaceImage[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onUpdated: () => void | Promise<void>
}

// ソート可能な画像アイテム
const SortableImageItem = ({ image, onDelete }: { image: PlaceImage; onDelete: (id: number) => void }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: image.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="relative aspect-square rounded-md bg-muted cursor-grab active:cursor-grabbing group touch-none hover:ring-2 hover:ring-primary/50 transition-all"
    >
      <AuthImage
        src={image.url}
        alt={`Image ${image.id}`}
        className="w-full h-full object-cover pointer-events-none rounded-md"
      />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onDelete(image.id)
        }}
        onPointerDown={(e) => e.stopPropagation()}
        className="p-0! absolute -top-1.5 -left-1.5 size-5 min-w-5 min-h-5 rounded-full bg-gray-500 hover:bg-gray-600 text-white flex items-center justify-center shadow-md cursor-pointer z-10"
      >
        <Minus className="size-3" strokeWidth={3} />
      </button>
    </div>
  )
}

export const PlaceImageManager = ({ placeId, images: initialImages, open, onOpenChange, onUpdated }: Props) => {
  const [images, setImages] = useState<PlaceImage[]>(initialImages)
  const { isSubmitting: isUploading, run } = useSubmitLock()
  const [activeTab, setActiveTab] = useState('list')
  const [uploadUrl, setUploadUrl] = useState('')
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null)

  // 外部からの更新を反映
  useEffect(() => {
    setImages(initialImages)
  }, [initialImages])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    setImages((items) => {
      const oldIndex = items.findIndex((item) => item.id === active.id)
      const newIndex = items.findIndex((item) => item.id === over.id)
      const newItems = arrayMove(items, oldIndex, newIndex)

      // 順序変更を即座にAPIに送信（楽観的UI）
      reorderPlaceImages(
        placeId,
        newItems.map((img) => img.id),
      )
        .then(() => {
          // 親コンポーネント（PlaceSheet等）に更新を通知してデータを再取得させる
          onUpdated()
        })
        .catch(() => {
          toast.error('並び順の保存に失敗しました')
          // 失敗したら元に戻す処理が必要だが、今回は省略
        })

      return newItems
    })
  }

  const handleDelete = async (imageId: number) => {
    const prevImages = images
    setDeleteTargetId(null)
    // 楽観的UI: 即座にローカルステートから削除
    setImages((items) => items.filter((img) => img.id !== imageId))
    try {
      await deletePlaceImage(placeId, imageId)
      await onUpdated()
    } catch {
      toast.error('画像の削除に失敗しました')
      setImages(prevImages)
    }
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return

    await run(async () => {
      const toastId = toast.loading('アップロード中...')
      try {
        const files = Array.from(e.target.files ?? [])
        await addPlaceImageFile(placeId, files)
        toast.success(`${files.length}枚の画像をアップロードしました`, { id: toastId })
        await onUpdated()
        // inputをリセット
        e.target.value = ''
        setActiveTab('list') // アップロード後はリストを表示
      } catch {
        toast.error('アップロードに失敗しました', { id: toastId })
      }
    })
  }

  const handleUrlUpload = async () => {
    if (!uploadUrl) return

    await run(async () => {
      const toastId = toast.loading('画像をダウンロード中...')
      try {
        await addPlaceImageUrl(placeId, uploadUrl)
        toast.success('画像を登録しました', { id: toastId })
        setUploadUrl('')
        await onUpdated()
        setActiveTab('list')
      } catch {
        toast.error('URLからの登録に失敗しました', { id: toastId })
      }
    })
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>画像管理</DialogTitle>
            <DialogDescription>画像の追加・並び替えができます。</DialogDescription>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="list">一覧・並び替え</TabsTrigger>
              <TabsTrigger value="add">画像の追加</TabsTrigger>
            </TabsList>

            <TabsContent value="list" className="flex-1 overflow-y-auto min-h-75 p-1 mt-4">
              {images.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground text-sm space-y-4">
                  <p>画像が登録されていません</p>
                  <Button variant="outline" onClick={() => setActiveTab('add')}>
                    画像を追加する
                  </Button>
                </div>
              ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={images} strategy={rectSortingStrategy}>
                    <div className="grid grid-cols-3 gap-3 p-1.5">
                      {images.map((image) => (
                        <SortableImageItem key={image.id} image={image} onDelete={setDeleteTargetId} />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </TabsContent>

            <TabsContent value="add" className="space-y-6 mt-4 p-1">
              <div className="space-y-4 border rounded-lg p-4 bg-muted/30">
                <div className="grid w-full items-center gap-2">
                  <Label htmlFor="picture" className="flex items-center gap-2">
                    <Upload className="size-4" />
                    ファイルからアップロード
                  </Label>
                  <Input
                    id="picture"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileUpload}
                    disabled={isUploading}
                    className="cursor-pointer file:cursor-pointer file:text-primary file:font-medium"
                  />
                  <p className="text-[10px] text-muted-foreground">
                    ※複数選択可。サーバー側でWebPに変換・最適化されます。
                  </p>
                </div>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">Or</span>
                </div>
              </div>

              <div className="space-y-4 border rounded-lg p-4 bg-muted/30">
                <div className="grid w-full items-center gap-2">
                  <Label htmlFor="url" className="flex items-center gap-2">
                    <Globe className="size-4" />
                    URLから追加
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="url"
                      type="url"
                      placeholder="https://example.com/image.jpg"
                      value={uploadUrl}
                      onChange={(e) => setUploadUrl(e.target.value)}
                      disabled={isUploading}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleUrlUpload()
                      }}
                    />
                    <Button type="button" onClick={handleUrlUpload} disabled={!uploadUrl || isUploading} size="icon">
                      {isUploading ? <Loader2 className="animate-spin size-4" /> : <Plus className="size-4" />}
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteTargetId !== null} onOpenChange={(open) => !open && setDeleteTargetId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>画像を削除しますか？</AlertDialogTitle>
            <AlertDialogDescription>この操作は取り消せません。画像は完全に削除されます。</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>キャンセル</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTargetId !== null && handleDelete(deleteTargetId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              削除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
